import React, { useState } from 'react';
import proj4 from 'proj4';
import Select from 'react-select';
import { MdClose } from 'react-icons/md';
import './MapSearch.css';
import { MAPBOX_TOKEN } from '../../config';

proj4.defs(
    "EPSG:8678",
    "+proj=tmerc +lat_0=0 +lon_0=18 +k=0.9999 +x_0=6500000 +y_0=0 +ellps=bessel +towgs84=682,-203,480,0,0,0,0 +units=m +no_defs +type=crs"
);

const selectStyles = {
    container: (base) => ({
        ...base,
        width: 'clamp(150px, 18vw, 240px)',
        minWidth: 0,
    }),
    control: (base, state) => ({
        ...base,
        minHeight: '38px',
        height: '38px',
        borderColor: state.isFocused ? '#3a3360' : '#ccc',
        boxShadow: state.isFocused ? '0 0 0 1px #3a3360' : 'none',
        '&:hover': {
            borderColor: '#3a3360',
        },
    }),
    valueContainer: (base) => ({
        ...base,
        height: '38px',
        padding: '0 8px',
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: '38px',
    }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isFocused ? '#3a3360' : 'white',
        color: state.isFocused ? 'white' : 'black',
        cursor: 'pointer',
    }),
    dropdownIndicator: (base, state) => ({
        ...base,
        transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : null,
        transition: 'transform 0.2s ease',
    }),
    menu: (base) => ({
        ...base,
        zIndex: 2000,
    }),
};

const MapSearch = ({ setViewState, setMarker, marker }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [coordinateSystem, setCoordinateSystem] = useState('4326');
    const [searchType, setSearchType] = useState('coordinates');
    const [options, setOptions] = useState([]);

    const moveToLocation = (longitude, latitude) => {
        setMarker({ latitude, longitude });

        setViewState(prev => ({
            ...prev,
            latitude,
            longitude,
            zoom: 14,
            transitionDuration: 1000,
        }));
    };

    const handleSearch = async (e) => {
        e.preventDefault();

        if (searchType === 'coordinates') {
            const [lon, lat] = searchTerm.split(',').map(value => Number(value.trim()));

            if (Number.isNaN(lon) || Number.isNaN(lat)) return;

            let coords;

            switch (coordinateSystem) {
                case '8678':
                    coords = proj4('EPSG:8678', 'EPSG:4326', [lon, lat]);
                    break;
                default:
                    coords = [lon, lat];
            }

            const [convertedLon, convertedLat] = coords;
            moveToLocation(convertedLon, convertedLat);
            return;
        }

        try {
            const response = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchTerm)}.json?access_token=${MAPBOX_TOKEN}`
            );

            const data = await response.json();

            if (data.features && data.features.length > 0) {
                const [lon, lat] = data.features[0].center;
                moveToLocation(lon, lat);
            }
        } catch (error) {
            console.error('Error fetching geocoding data:', error);
        }
    };

    const handleInputChange = async (inputValue) => {
        if (searchType !== 'place' || typeof inputValue !== 'string' || inputValue.length <= 2) {
            setOptions([]);
            return;
        }

        try {
            const response = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(inputValue)}.json?access_token=${MAPBOX_TOKEN}`
            );

            const data = await response.json();

            setOptions(
                data.features?.map(feature => ({
                    value: feature.place_name,
                    label: feature.place_name,
                    coordinates: feature.center,
                })) || []
            );
        } catch (error) {
            console.error('Error fetching geocoding data:', error);
            setOptions([]);
        }
    };

    const handleSelectChange = (selectedOption) => {
        if (!selectedOption?.coordinates) return;

        const [lon, lat] = selectedOption.coordinates;
        moveToLocation(lon, lat);
    };

    const handleReset = () => {
        setSearchTerm('');
        setOptions([]);
        setMarker(null);
    };

    return (
        <div className="map-search-container">
            <form onSubmit={handleSearch}>
                <Select
                    value={{
                        value: searchType,
                        label: searchType === 'coordinates'
                            ? 'Pretraga po koordinatama'
                            : 'Pretraga po nazivu',
                    }}
                    onChange={(option) => {
                        setSearchType(option.value);
                        setSearchTerm('');
                        setOptions([]);
                    }}
                    menuPlacement="top"
                    options={[
                        { value: 'coordinates', label: 'Pretraga po koordinatama' },
                        { value: 'place', label: 'Pretraga po nazivu' },
                    ]}
                    isSearchable={false}
                    styles={selectStyles}
                />

                {searchType === 'coordinates' && (
                    <Select
                        menuPlacement="top"
                        value={{
                            value: coordinateSystem,
                            label: `EPSG:${coordinateSystem}`,
                        }}
                        onChange={(option) => setCoordinateSystem(option.value)}
                        options={[
                            { value: '4326', label: 'EPSG:4326 (WGS 84)' },
                            { value: '8678', label: 'EPSG:8678 (Gauss-Kruger 6)' },
                        ]}
                        isSearchable={false}
                        styles={selectStyles}
                    />
                )}

                {searchType === 'coordinates' ? (
                    <input
                        className="input"
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Unesite koordinate (lon,lat)"
                        title="Unesite koordinate (lon,lat)"
                        required
                    />
                ) : (
                    <Select
                        menuPlacement="top"
                        options={options}
                        onInputChange={(inputValue) => {
                            setSearchTerm(inputValue);
                            handleInputChange(inputValue);
                        }}
                        onChange={handleSelectChange}
                        placeholder="Unesite adresu, mjesto..."
                        isClearable
                        styles={selectStyles}
                    />
                )}

                {marker ? (
                    <button
                        type="button"
                        onClick={handleReset}
                        title="Poništi pretragu"
                        className="map-search-reset-btn"
                    >
                        <MdClose />
                    </button>
                ) : (
                    <button type="submit">
                        Pretraži
                    </button>
                )}
            </form>
        </div>
    );
};

export default MapSearch;